/**
 * ExistingAssetTable Component
 *
 * A table component for displaying and managing uploaded assets. Features include:
 * - File preview with thumbnails for images and icons for other file types
 * - Sorting and filtering capabilities
 * - Pagination
 * - Global search
 * - File download functionality
 * - Loading states with progress indication
 */

import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  Table,
  Input,
  Progress,
  Image,
  IconButton,
  Pagination,
  SelectPicker,
  DatePicker,
} from 'rsuite';
import { debounce } from 'lodash';
import { FileDownload, Search, SortUp, SortDown, Sort } from '@rsuite/icons';
import { FileIcon, defaultStyles } from 'react-file-icon';
import Zoom from 'react-medium-image-zoom';

import { useOpalContext } from 'src/providers/OpalProvider';
import { useAssetUploadContext } from 'src/providers/AssetUploadProvider';

import styles from './ExistingAssetTable.module.scss';

const { Column, HeaderCell, Cell } = Table;

/**
 * Constants
 */

const IMAGE_TYPES = ['jpg', 'jpeg', 'png', 'gif']; // Supported image types for preview
const FILE_SIZE_UNITS = ['B', 'kB', 'MB', 'GB', 'TB']; // File size units for formatting
const DEFAULT_PAGE_SIZE = 100; // Default number of items per page
const DEBOUNCE_DELAY = 300; // Delay for debounced search and filter operations

/**
 * Utility Functions
 */

const downloadFile = (filename, content) => {
  const element = document.createElement('a');
  element.setAttribute('href', content);
  element.setAttribute('download', filename);
  element.style.display = 'none';
  document.body.appendChild(element);
  element.click();
  document.body.removeChild(element);
};

const formatFileSize = (fileSize) => {
  const i =
    fileSize === 0 ? 0 : Math.floor(Math.log(fileSize) / Math.log(1024));
  return `${(fileSize / 1024 ** i).toFixed(2)} ${FILE_SIZE_UNITS[i]}`;
};

const formatDate = (dateString) => {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  })
    .format(new Date(dateString + 'Z'))
    .replace(',', '');
};

/**
 * Sub-Components
 */
const LoadingProgress = ({ loadedPages, totalPages }) => (
  <div className={styles.loadingProgress}>
    <Progress.Line
      percent={Math.ceil((loadedPages / totalPages) * 100)}
      status="active"
      strokeWidth={24}
      strokeColor={'rgb(0 191 179)'}
      trailColor={'rgb(120 120 120)'}
    />
    <span className={styles.loadingText}>
      {totalPages <= 1 && loadedPages <= 1
        ? 'Loading...'
        : `${loadedPages}/${totalPages} pages loaded...`}
    </span>
  </div>
);

const FilePreview = ({ fileName, fileUrl }) => {
  const fileExt = fileName.split('.').pop().toLowerCase();
  return IMAGE_TYPES.includes(fileExt) ? (
    <Zoom>
      <Image fit alt={fileName} height={50} src={fileUrl} />
    </Zoom>
  ) : (
    <div className={styles.icon}>
      <FileIcon extension={fileExt} {...defaultStyles[fileExt]} />
    </div>
  );
};

const FilterInput = ({
  filterKey,
  title,
  value,
  onChange,
  onKeyDown,
  onBlur,
  type,
  options,
  isOpen,
  onClose,
}) => {
  if (type === 'date') {
    return (
      <DatePicker
        size="sm"
        placeholder="Select date..."
        value={value ? new Date(value) : null}
        onChange={(date) => {
          const localDate = date
            ? new Date(date.getTime() - date.getTimezoneOffset() * 60000)
            : null;
          const formattedDate = localDate
            ? localDate.toISOString().split('T')[0]
            : '';
          onChange(formattedDate);
        }}
        onClose={onClose}
        style={{ width: 200, zIndex: 1000 }}
        format="yyyy-MM-dd"
        cleanable
        open={isOpen}
        weekStart={0}
      />
    );
  }

  if (type === 'select') {
    return (
      <SelectPicker
        data={options}
        value={value}
        onChange={onChange}
        onClose={onClose}
        style={{ width: filterKey == 'status' ? 125 : 200, zIndex: 1000 }}
        placeholder="Filter..."
        searchable={false}
        cleanable
        open={isOpen}
      />
    );
  }

  return (
    <Input
      size="sm"
      placeholder={`Filter ${title.toLowerCase()}...`}
      value={value || ''}
      onChange={onChange}
      onKeyDown={onKeyDown}
      onBlur={onBlur}
      autoFocus
    />
  );
};

/**
 * Main Component
 */
const ExistingAssetTable = () => {
  const { permissions } = useOpalContext();
  const bypassWorkflowPermission = permissions.includes('bypassworkflow');
  const { rerender, setRerender } = useAssetUploadContext();
  const [rawDataCache, setRawDataCache] = useState([]);
  const [displayData, setDisplayData] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const TABLE_COLUMNS = [
    {
      key: 'fileName',
      title: 'File Name',
      flexGrow: 2,
      sortable: true,
      fullText: true,
      filterable: true,
      permission: true,
    },
    {
      key: 'serverUploadDate',
      title: 'Date Added',
      width: 165,
      sortable: true,
      filterable: true,
      permission: true,
    },
    {
      key: 'user',
      title: 'Added By',
      flexGrow: 2,
      sortable: true,
      fullText: true,
      filterable: true,
      permission: true,
    },
    {
      key: 'fileSize',
      title: 'Size',
      flexGrow: 1,
      sortable: true,
      fullText: true,
      filterable: false,
      permission: true,
    },
    {
      key: 'opalStatus',
      title: 'Status',
      width: 125,
      sortable: true,
      filterable: true,
      permission: true,
    },
    {
      key: 'bypassWorkflow',
      title: 'Skip WF',
      width: 80,
      sortable: false,
      filterable: false,
      permission: bypassWorkflowPermission,
    },
  ];

  // Filter and sort state
  const [sortColumn, setSortColumn] = useState('serverUploadDate');
  const [sortType, setSortType] = useState('desc');
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({});
  const [visibleFilters, setVisibleFilters] = useState({});
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
  const [debouncedFilters, setDebouncedFilters] = useState({});
  const [uniqueValues, setUniqueValues] = useState({
    user: [],
    status: [],
  });

  // Loading state
  const [isFirstPageLoaded, setIsFirstPageLoaded] = useState(false);
  const [isFullyLoaded, setIsFullyLoaded] = useState(false);
  const [loadedPages, setLoadedPages] = useState(0);

  // UI state
  const [editingFilter, setEditingFilter] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);

  /**
   * Data Fetching
   */
  const fetchPage = async (pageNum) => {
    const response = await axios.get(
      `/api/assets/pagesize/${limit}/page/${pageNum}`,
    );
    const { items, totalCount, totalPages } = response.data.message;

    if (pageNum === 1) {
      setTotalCount(totalCount);
      setTotalPages(totalPages);
    }

    return items;
  };

  /**
   * Search and Filter Handlers
   */
  const debouncedSearch = useMemo(
    () => debounce(setDebouncedSearchValue, DEBOUNCE_DELAY),
    [],
  );

  const debouncedFilterChange = useMemo(
    () =>
      debounce((key, value) => {
        setDebouncedFilters((prev) => ({ ...prev, [key]: value }));
      }, DEBOUNCE_DELAY),
    [],
  );

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    debouncedFilterChange(key, value);
    setPage(1);
  };

  const handleFilterKeyDown = (e) => {
    if (e.key === 'Enter') {
      setEditingFilter(null);
    }
  };

  const handleFilterBlur = () => {
    setEditingFilter(null);
  };

  const handleFilterClick = (filterKey) => {
    if (editingFilter === filterKey) {
      setEditingFilter(null);
      setOpenDropdown(null);
    } else {
      setEditingFilter(filterKey);
      setOpenDropdown(filterKey);
    }
  };

  /**
   * Table Rendering
   */
  const renderTableHeader = (key, title, isSortable = true) => {
    const column = TABLE_COLUMNS.find((col) => col.key === key);
    const isFilterable = column?.filterable ?? true;

    return (
      <HeaderCell style={{ padding: '8px' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            width: '100%',
            position: 'relative',
          }}
        >
          {editingFilter === key ? (
            <FilterInput
              filterKey={key}
              title={title}
              value={filters[key]}
              onChange={(value) => handleFilterChange(key, value)}
              onKeyDown={handleFilterKeyDown}
              onBlur={handleFilterBlur}
              type={
                key === 'serverUploadDate'
                  ? 'date'
                  : key === 'user' || key === 'opalStatus'
                    ? 'select'
                    : 'text'
              }
              options={uniqueValues[key]}
              isOpen={openDropdown === key}
              onClose={() => {
                setEditingFilter(null);
                setOpenDropdown(null);
              }}
            />
          ) : (
            <div className={styles.headerTitle} style={{ flex: 1 }}>
              {isSortable &&
                (sortColumn === key ? (
                  <span
                    style={{ marginRight: '-4px', color: 'rgb(0 191 179)' }}
                  >
                    {sortType === 'asc' ? <SortUp /> : <SortDown />}
                  </span>
                ) : (
                  <span style={{ marginRight: '-4px' }}>
                    <Sort />
                  </span>
                ))}
              {title}
            </div>
          )}
          {editingFilter !== key && isFilterable && (
            <IconButton
              icon={<Search style={{ fontSize: '12px' }} />}
              size="xs"
              appearance="subtle"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleFilterClick(key);
              }}
              className={`${styles.headerButton} ${filters[key] ? styles.activeFilter : ''}`}
              disabled={!isFullyLoaded}
            />
          )}
        </div>
      </HeaderCell>
    );
  };

  /**
   * Data Processing
   */
  const filteredAndSortedData = useMemo(() => {
    if (!isFirstPageLoaded) return [];

    let filtered = [...rawDataCache];

    // Apply global search
    if (debouncedSearchValue) {
      filtered = filtered.filter((item) =>
        Object.values(item).some((val) =>
          val
            ?.toString()
            .toLowerCase()
            .includes(debouncedSearchValue.toLowerCase()),
        ),
      );
    }

    // Apply column filters
    Object.entries(debouncedFilters).forEach(([key, value]) => {
      if (value) {
        if (key === 'serverUploadDate') {
          const filterDate = value.split('T')[0];
          filtered = filtered.filter((item) => {
            if (!item[key]) return false;
            const itemDate = new Date(item[key]);
            const localItemDate = new Date(
              itemDate.getTime() - itemDate.getTimezoneOffset() * 60000,
            )
              .toISOString()
              .split('T')[0];
            return localItemDate === filterDate;
          });
        } else {
          filtered = filtered.filter((item) =>
            item[key]?.toString().toLowerCase().includes(value.toLowerCase()),
          );
        }
      }
    });

    // Apply sorting
    if (sortColumn && sortType) {
      filtered.sort((a, b) => {
        let aVal = a[sortColumn];
        let bVal = b[sortColumn];

        if (sortColumn === 'fileSize') {
          aVal = Number(aVal) || 0;
          bVal = Number(bVal) || 0;
        } else if (sortColumn === 'serverUploadDate') {
          aVal = new Date(aVal).getTime();
          bVal = new Date(bVal).getTime();
        } else {
          aVal = (aVal?.toString() || '').toLowerCase();
          bVal = (bVal?.toString() || '').toLowerCase();
        }

        if (aVal === bVal) return 0;
        return sortType === 'asc'
          ? aVal > bVal
            ? 1
            : -1
          : aVal < bVal
            ? 1
            : -1;
      });
    }

    return filtered;
  }, [
    rawDataCache,
    debouncedSearchValue,
    debouncedFilters,
    sortColumn,
    sortType,
    isFirstPageLoaded,
  ]);

  /**
   * Data Loading Effects
   */
  useEffect(() => {
    const loadAndPrefetch = async () => {
      try {
        // Load first page
        const firstPage = await fetchPage(1);
        setDisplayData(firstPage);
        setRawDataCache(firstPage);
        setLoadedPages(1);

        // Get total pages
        const response = await axios.get(
          `/api/assets/pagesize/${limit}/page/1`,
        );

        const { totalPages: newTotalPages } = response.data.message;
        setTotalPages(newTotalPages);
        setIsFirstPageLoaded(true);

        // Prefetch remaining pages
        const allPages = Array(newTotalPages).fill(null);
        allPages[0] = firstPage;

        const fetches = Array.from({ length: newTotalPages - 1 }, (_, i) => {
          const pageNum = i + 2;
          return fetchPage(pageNum)
            .then((items) => {
              allPages[pageNum - 1] = items;
              setLoadedPages((prev) => prev + 1);
            })
            .catch((err) => {
              console.warn(`Failed to load page ${pageNum}`, err);
              allPages[pageNum - 1] = [];
              setLoadedPages((prev) => prev + 1);
            });
        });

        await Promise.all(fetches);

        // Process all loaded data
        const allItems = allPages.flat();
        setRawDataCache(allItems);
        setTotalCount(allItems.length);

        // Extract unique values for dropdowns
        const uniqueUploaders = [...new Set(allItems.map((item) => item.user))]
          .filter(Boolean)
          .sort();
        const uniqueStatuses = [
          ...new Set(allItems.map((item) => item.opalStatus)),
        ]
          .filter(Boolean)
          .sort();
        setUniqueValues({
          user: uniqueUploaders.map((value) => ({ label: value, value })),
          status: uniqueStatuses.map((value) => ({ label: value, value })),
        });

        setIsFullyLoaded(true);
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };

    loadAndPrefetch();
  }, [rerender, limit]);

  // Update displayed data when filters, sort, or pagination changes
  useEffect(() => {
    const start = (page - 1) * limit;
    const end = start + limit;
    setDisplayData(filteredAndSortedData.slice(start, end));
  }, [filteredAndSortedData, page, limit]);

  /**
   * Component Render
   */
  return (
    <div className={styles.container}>
      {/* Header Section */}
      <div className={styles.header}>
        <h6>Recently Uploaded Files</h6>
        <div className={styles.searchContainer}>
          {!isFullyLoaded ? (
            <LoadingProgress
              loadedPages={loadedPages}
              totalPages={totalPages}
            />
          ) : (
            <Input
              placeholder="Global search..."
              value={search}
              onChange={(value) => {
                setSearch(value);
                debouncedSearch(value);
                setPage(1);
              }}
              style={{ marginBottom: 10 }}
            />
          )}
        </div>
      </div>

      {/* Filter Row */}
      <div className={styles.filterRow}>
        <div className={styles.filterSpacer} />
        {TABLE_COLUMNS.filter((column) => column.filterable).map(({ key }) => (
          <div key={key} className={styles.filterInput}>
            {visibleFilters[key] && (
              <Input
                size="sm"
                placeholder={`Filter ${key.replace(/([A-Z])/g, ' $1').toLowerCase()}...`}
                value={filters[key] || ''}
                onChange={(value) => handleFilterChange(key, value)}
                disabled={!isFirstPageLoaded}
              />
            )}
          </div>
        ))}
        <div className={styles.downloadSpacer} />
      </div>

      {/* Main Table */}
      <Table
        data={displayData}
        autoHeight
        rowHeight={80}
        sortColumn={sortColumn}
        sortType={sortType}
        onSortColumn={(col, type) => {
          if (!isFirstPageLoaded || editingFilter) return;
          setSortColumn(col);
          setSortType(type);
          setPage(1);
        }}
        loading={!isFirstPageLoaded}
        style={{
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
          borderTop: 0,
        }}
      >
        {/* Preview Column */}
        <Column width={75} align="center">
          <HeaderCell>Preview</HeaderCell>
          <Cell>
            {(rowData) => (
              <div className={styles.previewCell}>
                <FilePreview
                  fileName={rowData.fileName}
                  fileUrl={rowData.fileUrl}
                />
              </div>
            )}
          </Cell>
        </Column>

        {/* Dynamic Columns */}
        {TABLE_COLUMNS.map(
          ({ key, title, width, flexGrow, sortable, fullText, permission }) =>
            permission ? (
              <Column
                key={key}
                {...(flexGrow ? { flexGrow } : { width })}
                sortable={isFullyLoaded && sortable}
                fullText={fullText}
              >
                {renderTableHeader(key, title, sortable)}
                <Cell dataKey={key}>
                  {key === 'serverUploadDate'
                    ? (rowData) => (
                        <div className={styles.dateCell}>
                          {' '}
                          {formatDate(rowData.serverUploadDate)}{' '}
                        </div>
                      )
                    : key === 'fileSize'
                      ? (rowData) => formatFileSize(rowData.fileSize)
                      : key === 'bypassWorkflow'
                        ? (rowData) => (rowData.bypassWorkflow ? 'Yes' : 'No')
                        : key === 'opalStatus'
                          ? (rowData) => (
                              <div className={styles.dateCell}>
                                {rowData.opalStatus}
                              </div>
                            )
                          : undefined}
                </Cell>
              </Column>
            ) : null,
        )}

        {/* Download Column */}
        <Column width={50} align="center">
          <HeaderCell> </HeaderCell>
          <Cell>
            {(rowData) => (
              <IconButton
                icon={<FileDownload />}
                onClick={() => downloadFile(rowData.fileName, rowData.fileUrl)}
                size="sm"
                appearance="link"
                title="Download"
                style={{
                  color: 'rgb(0 191 179)',
                  margin: '0',
                  backgroundColor: 'transparent',
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                }}
              />
            )}
          </Cell>
        </Column>
      </Table>

      {/* Pagination */}
      {isFirstPageLoaded && (
        <div className={styles.paginationContainer}>
          <Pagination
            prev
            next
            first
            last
            ellipsis
            boundaryLinks
            maxButtons={5}
            size="md"
            layout={['total', '-', 'limit', '|', 'pager', 'skip']}
            total={filteredAndSortedData.length}
            limitOptions={[10, 30, 50, 100]}
            limit={limit}
            activePage={page}
            onChangePage={setPage}
            onChangeLimit={setLimit}
          />
        </div>
      )}
    </div>
  );
};

export default ExistingAssetTable;
